import type { ChangeHistoryValue } from "../components/ChangeHistory";
import type { OrganizationRouteRelevance } from "@vendistri/ui";
import type { BusinessInformation, Task, TaskStatus } from "../types";
export const GROWTH_CHANGED = "vendistri:growth-changed";
export type GrowthChoice = { value: string; label: string; avatarUrl?: string | null };
export type GrowthSettings = {
  defaultAssigneeId: string | null;
  defaultAssigneeName: string;
  defaultAssigneeEmail: string;
  unavailable: boolean;
  revision: number;
  canManage?: boolean;
  members?: GrowthChoice[];
};
export type GrowthOptions = {
  canManage?: boolean;
  stages: GrowthChoice[];
  activities: GrowthChoice[];
  outcomes: GrowthChoice[];
  taskActions: GrowthChoice[];
  workModes: GrowthChoice[];
  members: GrowthChoice[];
};
export type GrowthLead = {
  starred?: boolean;
  searchMatchReasons?: { label: string; value: string }[];
  businessName: string;
  description: string;
  sourceDescription?: string | null;
  organizationRelevance?: OrganizationRouteRelevance | null;
  addressLines: string[];
  postalCode: string;
  latitude: number;
  longitude: number;
  timezone: string;

  id: string;
  entitlementId: string;
  opportunityId: string;
  title: string;
  area: string;
  locality: string;
  administrativeArea: string;
  countryCode: string;
  stage: string;
  assignedToId: string | null;
  assignedToName: string;
  assignedToEmail?: string;
  assignmentMode?: "organization" | "person" | "unassigned";
  assignmentOverrideId?: string | null;
  assignmentUnavailable?: boolean;
  nextFollowUpAt: string | null;
  manualNextFollowUpAt?: string | null;
  acquiredAt: string;
  attention: string[];
};
export type GrowthMapLead = Pick<GrowthLead, "id" | "title" | "stage" | "latitude" | "longitude" | "assignedToId" | "addressLines" | "locality" | "administrativeArea" | "postalCode" | "countryCode"> & {
  canManage: boolean; canWork: boolean;
};
export type GrowthPurchase = {
  refundedCashCents?: number;
  netCashCents?: number;
  orderId: string;
  orderLineId: string;
  productName: string;
  currency: string;
  subtotalCents: number;
  discountCents: number;
  creditCents: number;
  allowanceCents: number;
  allowancePoints: number;
  cashCents: number;
  paidAt: string | null;
  fulfilledAt: string | null;
  purchaser: { id: string; name: string; email: string };
  charge: { id: string; status: string; amountCents: number; paymentMethod: string } | null;
  expense: { id: string; status: string; amountCents: number } | null;
};
export type GrowthLeadDetail = GrowthLead & {
  liveTask?: Pick<Task, "id" | "type" | "status" | "isLive" | "startedAt" | "doneAt"> | null;
  businessInformation?: BusinessInformation;
  suppliedBusinessInformation?: BusinessInformation | null;
  businessInformationEdited?: boolean;
  updatedAt?: string;
  appointmentBookingUnavailableReason?: string;
  appointmentQualification?: { confirmedAt: string; requestedAction: string } | null;
  addedContacts?: GrowthAddedContact[];
  addedPhotos?: GrowthAddedPhoto[];
  purchase?: GrowthPurchase | null;
  media?: { id: string; altText?: string; originalUrl: string }[];
  lostReason: string;
  convertedLocationId: string | null;
  canManage: boolean;
  canWork: boolean;
  contacts: {
    status?: "current" | "superseded";
    supersededById?: string | null;
    id: string;
    name: string;
    role: string;
    contactPoints: {
      type: string;
      value: string;
      label: string;
      verificationStatus: string;
    }[];
  }[];
};
export type GrowthLeadWork = Pick<GrowthLeadDetail,
  "id" | "title" | "businessName" | "area" | "nextFollowUpAt" |
  "appointmentQualification" | "contacts" | "addedContacts" | "canWork"
> & { entitlementId?: string };
export type GrowthLeadPage = {
  items: GrowthLead[];
  totalCount: number;
  nextCursor: string | null;
};
export type GrowthDashboard = {
  total: number;
  new: number;
  unassigned: number;
  dueToday: number;
  overdue: number;
  active: number;
  meetingsScheduled: number;
  won: number;
  converted: number;
  generatedAt: string;
};
export type GrowthActivity = {
  details?: { taskId?: string; orderId?: string | null; changes?: ChangeHistoryValue[] };
  actorName?: string;
  id: string;
  type: string;
  outcome: string;
  summary: string;
  occurredAt: string;
  actorId: string | null;
};
export type GrowthTask = Pick<Task, "isLive" | "startedAt" | "doneAt"> & {
  notes?: string | null;
  assignee: string | null;
  id: string;
  title: string;
  action: string;
  customAction: string;
  workMode: "remote" | "field";
  status: TaskStatus;
  scheduledFor: string;
  assigneeName: string | null;
  assigneeEmail: string | null;
};
export type GrowthFeed<T> = { items: T[]; nextCursor: string | null };

export type GrowthAddedContact = { id?: string; name: string; role: string; email: string; phone: string };
export type GrowthAddedPhoto = { id: string; caption: string; originalUrl: string; createdAt: string };
export type GrowthOverview = {
  needsAttention: number;
  followUpsScheduled: number;
  summary: GrowthDashboard;
  pipeline: { stage: string; label: string; count: number }[];
  acquiredInPeriod: number;
  attentionCount: number;
  attention: GrowthLead[];
  scheduledTasks: number;
  upcomingTasks: {
    id: string;
    leadId: string;
    leadTitle: string;
    title: string;
    scheduledFor: string;
    assignee: string;
    status: string;
  }[];
  recentActivity: {
    id: string;
    leadId: string;
    leadTitle: string;
    summary: string;
    type: string;
    occurredAt: string;
    actor: string;
  }[];
  financial: {
    availablePoints: number;
    openClaims: number;
    totals: {
      currency: string;
      subtotalCents: number;
      cashCents: number;
      discountCents: number;
      allowanceCents: number;
      creditCents: number;
      allowancePoints: number;
    }[];
  } | null;
  period: string;
  periodStart: string | null;
  periodEnd: string;
};
